import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { KeyboardArrowRight } from "@mui/icons-material";
import { HELP_CLOSE_EVENT, moduleTabs } from "../../../data/workspaceConfig";

export function MetricCard({ item }) {
  return (
    <article className="metric-card">
      <span>{item.label}</span>
      <strong>{item.value}</strong>
      <small>{item.change}</small>
    </article>
  );
}

export function DashboardTabs({ activeTab, setActiveTab }) {
  const tabs = moduleTabs.Dashboard || ["Overview", "My Activity", "Team Activity", "Approvals", "Analytics"];

  return (
    <div className="module-subtabs" aria-label="Dashboard sections">
      {tabs.map((tab) => (
        <button
          className={activeTab === tab ? "active" : ""}
          key={tab}
          onClick={() => setActiveTab(tab)}
          type="button"
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

export function ModuleTabStrip({ moduleName, activeTab, setActiveTab, tabs: providedTabs }) {
  const tabs = (providedTabs || moduleTabs[moduleName] || []).map((tab, index) => {
    if (typeof tab === "string") {
      return {
        key: `${moduleName}-${tab}-${index}`,
        name: tab,
        label: tab,
        enabled: true,
        premium: false,
      };
    }
    return {
      key: tab.key || `${moduleName}-${tab.name || tab.label || index}`,
      name: tab.name || tab.label || `Tab ${index + 1}`,
      label: tab.label || tab.name || `Tab ${index + 1}`,
      enabled: tab.enabled !== false,
      premium: tab.premium === true,
    };
  });
  if (!tabs.length) return null;

  return (
    <div className="module-subtabs" aria-label={`${moduleName} sections`}>
      {tabs.map((tab) => (
        <button
          className={`${activeTab === tab.name ? "active" : ""} ${tab.premium ? "premium" : ""} ${tab.enabled === false ? "locked" : ""}`.trim()}
          disabled={tab.enabled === false}
          key={tab.key}
          onClick={() => {
            if (tab.enabled === false) return;
            setActiveTab(tab.name);
          }}
          type="button"
        >
          {tab.label}
          {tab.premium ? <small>Premium</small> : null}
        </button>
      ))}
    </div>
  );
}

export function HelpModal({ title, children, onClose }) {
  return createPortal(
    <div className="context-modal-backdrop" role="presentation">
      <section className="context-modal" role="dialog" aria-modal="true" aria-label={`${title} context`}>
        <header>
          <div>
            <span>Context from ?</span>
            <h3>{title}</h3>
          </div>
          <button onClick={onClose} type="button">Close</button>
        </header>
        <div className="context-modal-body">{children}</div>
      </section>
    </div>,
    document.body
  );
}

export function HelpTrigger({ title, children }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const closeHelp = () => setOpen(false);
    window.addEventListener(HELP_CLOSE_EVENT, closeHelp);
    return () => window.removeEventListener(HELP_CLOSE_EVENT, closeHelp);
  }, []);

  return (
    <>
      <button
        aria-label={`Open help for ${title}`}
        className="heading-help"
        onClick={() => setOpen(true)}
        title={`About ${title}`}
        type="button"
      >
        ?
      </button>
      {open ? <HelpModal title={title} onClose={() => setOpen(false)}>{children}</HelpModal> : null}
    </>
  );
}

export function ContextPoints({ points = [] }) {
  if (!points.length) return null;

  return (
    <div className="context-doc-points">
      {points.map((point) => (
        <p key={point}>{point}</p>
      ))}
    </div>
  );
}

export function SectionHeader({ eyebrow, title, text }) {
  return (
    <div className="section-header">
      <span>{eyebrow}</span>
      <div className="section-title-row"><h2>{title}</h2></div>
      {text ? <p className="section-context-text">{text}</p> : null}
    </div>
  );
}

export function EmptyActionBar({ primary = "Create", secondary = "Import" }) {
  return (
    <div className="module-actions">
      <button className="primary-action" type="button">
        {primary}
        <KeyboardArrowRight fontSize="small" />
      </button>
      <button className="secondary-action" type="button">{secondary}</button>
    </div>
  );
}

export function SubtabPanel({ moduleName, activeTab, primary = "Create", children }) {
  return (
    <section className="module-header-card">
      <SectionHeader
        eyebrow={`${moduleName} · ${activeTab}`}
        title={`${activeTab} workspace`}
        text={`Manage ${activeTab.toLowerCase()} inside ${moduleName} with filters, ownership, status, reports, and team actions.`}
      />
      <EmptyActionBar primary={primary} secondary="Filter" />
      {children ? <div className="subtab-inline-content">{children}</div> : null}
    </section>
  );
}
